import React, { useEffect, useRef, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const labelCls = "block text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5";
const inputCls = "h-11 rounded-xl bg-white";

function today() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function SearchSelect({ options, value, onChange, placeholder, tid, allowCustom = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const matches = q.trim() ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : options.slice(0, 8);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) { setOpen(false); setQ(""); }
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => document.removeEventListener("pointerdown", closeOutside, true);
  }, [open]);
  return (
    <div className="relative" ref={rootRef}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid={tid}
          className={`w-full text-left h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm truncate hover:border-blue-300 ${value ? "text-slate-900" : "text-slate-400"}`}>
          {value || placeholder}
        </button>
      ) : (
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={`${tid}-search`}
          onKeyDown={e => {
            if (e.key === "Escape") { setOpen(false); setQ(""); }
            if (e.key === "Enter") { e.preventDefault(); if (q.trim()) { onChange(q.trim()); setOpen(false); setQ(""); } }
          }} />
      )}
      {open && (
        <div className="absolute z-20 inset-x-0 mt-1 bg-white border border-slate-200 rounded-xl max-h-52 overflow-y-auto shadow-lg">
          {matches.map(o => (
            <button type="button" key={o} onClick={() => { onChange(o); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 truncate">{o}</button>
          ))}
          {allowCustom && q.trim() && !matches.includes(q.trim()) && (
            <button type="button" onClick={() => { onChange(q.trim()); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2.5 text-sm text-blue-600 font-medium hover:bg-blue-50">
              Use custom: "{q.trim()}"
            </button>
          )}
          {matches.length === 0 && !q.trim() && <div className="px-3 py-3 text-xs text-slate-500">Type to search or add a custom value</div>}
        </div>
      )}
    </div>
  );
}

function ContainerPicker({ catalog, value, onSelect, tid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const selected = catalog.find(c => c.id === value);
  const matches = q.trim()
    ? catalog.filter(c => `${c.name} ${c.category} ${c.class_code} ${c.sl_no ?? ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : catalog.slice(0, 8);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) { setOpen(false); setQ(""); }
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => document.removeEventListener("pointerdown", closeOutside, true);
  }, [open]);
  return (
    <div className="relative" ref={rootRef}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid={tid}
          className={`w-full text-left h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm truncate hover:border-blue-300 ${selected ? "text-slate-900" : "text-slate-400"}`}>
          {selected ? `${selected.name} · ${selected.category}` : "Search container by model, class or Sl No…"}
        </button>
      ) : (
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search container…" className={inputCls} data-testid={`${tid}-search`} />
      )}
      {open && (
        <div className="absolute z-20 inset-x-0 mt-1 bg-white border border-slate-200 rounded-xl max-h-56 overflow-y-auto shadow-lg">
          {matches.map(c => (
            <button type="button" key={c.id} onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2.5 hover:bg-slate-50" data-testid={`${tid}-option-${c.id}`}>
              <div className="text-sm font-medium text-slate-900 truncate">
                {c.sl_no != null && <span className="font-mono-tab text-slate-400 mr-1.5">#{c.sl_no}</span>}{c.name}
              </div>
              <div className="text-xs text-slate-500">{c.category}{c.class_code ? ` · ${c.class_code}` : ""}{c.final_price ? ` · Customer rate ₹${Number(c.final_price).toLocaleString("en-IN")}` : ""}</div>
            </button>
          ))}
          {matches.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">No containers match "{q}"</div>}
        </div>
      )}
    </div>
  );
}

const DEFAULT_SPECS_BY_MODEL = {
  "GRC F": { inside: "SS finish", outside: "GRP finish", rear_door: "Fish Type Door", floor: "SS Corrugated floor", core: "", insulation: "100 mm" },
  "ALP F": { inside: "SS finish", outside: "Aluminum Plain finish", rear_door: "Fish Type Door", floor: "SS Corrugated floor", core: "", insulation: "100 mm" },
  "ALC F": { inside: "SS finish", outside: "Aluminum Corrugation finish", rear_door: "Fish Type Door", floor: "SS Corrugated floor", core: "", insulation: "100 mm" },
  "GRC": { inside: "SS finish", outside: "GRP finish", rear_door: "Container Type Door", floor: "SS Corrugated floor", core: "", insulation: "100 mm" },
  "GRC ZeCon": { inside: "SS finish", outside: "GRP finish", rear_door: "Container Type Door", floor: "SS Corrugated floor", core: "", insulation: "100 mm" },
  "GFC 50": { inside: "GRP finish", outside: "GRP finish", rear_door: "Container Type Door", floor: "SS Chequered floor", core: "", insulation: "50 mm" },
  "GFC 30": { inside: "GRP finish", outside: "GRP finish", rear_door: "Container Type Door", floor: "SS Chequered floor", core: "", insulation: "30 mm" },
  "HKC": { inside: "Galvalume finish", outside: "GRP finish", rear_door: "Container Type Door", floor: "Aluminum Plank Floor", core: "", insulation: "" },
  "PFC": { inside: "MS Corrugated finish", outside: "GRP Plain finish", rear_door: "Container Type Door", floor: "MS Plain Floor", core: "", insulation: "" },
  "MDC": { inside: "MS Corrugated finish", outside: "MS Corrugated finish", rear_door: "Container Type Door", floor: "MS Plain floor", core: "", insulation: "" },
};

const SPEC_FIELDS = [
  ["Inside Finish", "inside"],
  ["Out Side Finish", "outside"],
  ["Rear Door", "rear_door"],
  ["Floor", "floor"],
  ["Core (optional)", "core"],
  ["Insulation Thickness (optional)", "insulation"],
];


const rateAmountInWords = (value) => {
  const raw = String(value ?? "").replace(/,/g, "").trim();
  if (!raw) return "";
  if (!/^\d+(?:\.\d{0,2})?$/.test(raw)) return "Enter a valid amount (up to two decimal places)";
  const whole = raw.split(".")[0].replace(/^0+(?=\d)/, "");
  if (whole.length > 15) return "Amount is too large";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const underHundred = n => n < 20 ? ones[n] : [tens[Math.floor(n / 10)], ones[n % 10]].filter(Boolean).join(" ");
  const underThousand = n => [
    n >= 100 ? ones[Math.floor(n / 100)] + " Hundred" : "",
    n % 100 ? underHundred(n % 100) : ""
  ].filter(Boolean).join(" ");
  const groups = ["", "Thousand", "Lakh", "Crore", "Arab", "Kharab", "Neel"];
  let n = BigInt(whole || "0");
  const chunks = [];
  const first = Number(n % 1000n);
  if (first) chunks.push(underThousand(first));
  n /= 1000n;
  let idx = 1;
  while (n > 0n && idx < groups.length) {
    const part = Number(n % 100n);
    if (part) chunks.unshift(underHundred(part) + " " + groups[idx]);
    n /= 100n;
    idx++;
  }
  const rupees = chunks.join(" ") || "Zero";
  const paise = Number((raw.split(".")[1] || "").padEnd(2, "0"));
  return rupees + " Rupees" + (paise ? " and " + underHundred(paise) + " Paise" : "") + " Only";
};

export default function QuotationBuilderPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = (searchParams.get("edit") || window.location.pathname.match(/\/quotes\/edit\/([^/]+)/)?.[1] || null);

  const [catalog, setCatalog] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [specOpts, setSpecOpts] = useState({});
  const [addonOpts, setAddonOpts] = useState([]);
  const [nextSerial, setNextSerial] = useState(null);

  const [serialCustom, setSerialCustom] = useState("");
  const [date, setDate] = useState(today());
  const [customer, setCustomer] = useState({ customer_name: "", customer_address: "", customer_phone: "", customer_gst: "" });
  const [mainC, setMainC] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [specs, setSpecs] = useState({ external: "", inside: "", outside: "", rear_door: "", floor: "", core: "", insulation: "" });
  const [extras, setExtras] = useState([]);
  const [qty, setQty] = useState(1);
  const [rate, setRate] = useState("");
  const [addonRows, setAddonRows] = useState([]);
  const [delivery, setDelivery] = useState("15-30");
  const [saving, setSaving] = useState(false);
  const workDraftSource=editId ? "edit-"+editId : "quotation"; // quote-draft-menu-20260921
  const saveWorkDraft=async()=>{
    try{
      await api.post(`/work-drafts/quotation/${workDraftSource}`,{payload:{serialCustom, date, customer, mainC, vehicle, specs, extras, qty, rate, addonRows, delivery}});
      toast.success("Quotation draft saved");
    }catch(e){toast.error(e.response?.data?.detail||"Could not save quotation draft")}
  };
  const restoreWorkDraft=async()=>{
    try{
      const r=await api.get(`/work-drafts/quotation/${workDraftSource}`);
      const d=r.data?.payload;
      if(!d)return;
      if (Object.prototype.hasOwnProperty.call(d,"serialCustom")) setSerialCustom(d["serialCustom"]);
      if (Object.prototype.hasOwnProperty.call(d,"date")) setDate(d["date"]);
      if (Object.prototype.hasOwnProperty.call(d,"customer")) setCustomer(d["customer"]);
      if (Object.prototype.hasOwnProperty.call(d,"mainC")) setMainC(d["mainC"]);
      if (Object.prototype.hasOwnProperty.call(d,"vehicle")) setVehicle(d["vehicle"]);
      if (Object.prototype.hasOwnProperty.call(d,"specs")) setSpecs(d["specs"]);
      if (Object.prototype.hasOwnProperty.call(d,"extras")) setExtras(d["extras"]);
      if (Object.prototype.hasOwnProperty.call(d,"qty")) setQty(d["qty"]);
      if (Object.prototype.hasOwnProperty.call(d,"rate")) setRate(d["rate"]);
      if (Object.prototype.hasOwnProperty.call(d,"addonRows")) setAddonRows(d["addonRows"]);
      if (Object.prototype.hasOwnProperty.call(d,"delivery")) setDelivery(d["delivery"]);
      toast.message("Quotation draft restored");
    }catch(e){}
  };
  useEffect(()=>{const t=setTimeout(restoreWorkDraft,900);return()=>clearTimeout(t)},[]);


  useEffect(() => {
    api.get("/containers").then(r => setCatalog(r.data));
    api.get("/vehicles").then(r => setVehicleList(r.data.map(v => v.name)));
    api.get("/spec-options").then(r => setSpecOpts(r.data));
    api.get("/addons").then(r => setAddonOpts(r.data));
    if (!editId) api.get("/quotations/next-serial").then(r => setNextSerial(r.data.serial));
  }, [editId]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/quotations/${editId}`).then(r => {
      const q = r.data;
      setCustomer({ customer_name: q.customer_name, customer_address: q.customer_address || "", customer_phone: q.customer_phone || "", customer_gst: q.customer_gst || "" });
      const m = q.items[0];
      if (m) {
        setMainC(m.container_id || "");
        setVehicle(m.vehicle_name || "");
        setSpecs({ external: "", inside: "", outside: "", rear_door: "", floor: "", core: "", insulation: "", ...(m.specs || {}) });
        setExtras(m.specs?.extra || []);
        setQty(m.qty || 1);
        setRate((Number(m.rate || m.unit_price || 0) * 1.18).toFixed(2).replace(/\.00$/, ""));
      }
      setAddonRows(q.items.slice(1).map(a => ({ type: a.addon_type || "Accessory", description: a.description || a.product_name || "", qty: a.qty || 1, rate: (Number(a.rate || a.unit_price || 0) * 1.18).toFixed(2).replace(/\.00$/, ""), tax_mode: "after" })));
      setDelivery(q.delivery_days || "15-30");
      if (q.quote_date) setDate(q.quote_date);
      setNextSerial(q.serial_no);
    }).catch(() => toast.error("Could not load quotation"));
  }, [editId]);

  const pickContainer = (c) => {
    setMainC(c.id);
    setRate(c.final_price || (c.standard_price ? Math.round(Number(c.standard_price) * 1.18 * 100) / 100 : ""));
    const d = { ...(DEFAULT_SPECS_BY_MODEL[c.model] || {}) };
    if (c.model === "GFC 50" && c.class_code === "L1") d.floor = "SS Corrugated floor";
    setSpecs({
      external: (c.external_dimensions || "").replace(/\s*mm\s*$/i, ""),
      inside: d.inside || c.interior || "",
      outside: d.outside || c.exterior || "",
      rear_door: d.rear_door || c.rear_door || "",
      floor: d.floor || c.flooring_remarks || "",
      core: d.core || c.core || "",
      insulation: d.insulation || c.insulation || "",
    });
  };

  const addonMasterType = (a) => {
    const raw=[a?.type,a?.addon_type,a?.category,a?.kind].filter(Boolean).join(" ").toLowerCase();
    if (/reefer|refer|refrigerat/.test(raw)) return "Reefer Unit";
    if (/accessor/.test(raw)) return "Accessory";
    if (/other|charge/.test(raw)) return "Other Charge";
    return String(a?.type||"");
  };
  const addonMasterName = (a) => String(a?.name||a?.model||a?.title||a?.addon_name||a?.reefer_name||"").trim();
  const addonMasterDisplay = (a) => a.description ? `${addonMasterName(a)} — ${a.description}` : addonMasterName(a);
  const addonDescriptions = (type) =>
    addonOpts
      .filter(a => a && a.active!==false && a.is_active!==false && !a.archived_at && addonMasterType(a) === type)
      .map(addonMasterDisplay)
      .filter(Boolean);
  const addonTaxMode = (a) => a?.tax_mode==="before" ? "before" : "after";
  const addonRateAfterGST = (a) => {
    const n=Number(a?.rate);
    if(!Number.isFinite(n)) return 0;
    return addonTaxMode(a)==="before" ? Math.round(n*1.18*100)/100 : n;
  };
  const addonRateBeforeGST = (a) => {
    const n=Number(a?.rate);
    if(!Number.isFinite(n)) return 0;
    return addonTaxMode(a)==="before" ? n : Math.round((n/1.18)*100)/100;
  };
  const addonMasterRateForMode = (a,mode) => {
    if(!a) return "";
    const basicRaw=a.standard_price!=null && a.standard_price!=="" ? a.standard_price : a.price;
    const finalRaw=a.final_price;
    const basic=Number(basicRaw), final=Number(finalRaw);
    if(mode==="before"){
      if(basicRaw!=null && basicRaw!=="" && Number.isFinite(basic)) return Math.round(basic*100)/100;
      if(finalRaw!=null && finalRaw!=="" && Number.isFinite(final)) return Math.round((final/1.18)*100)/100;
      return "";
    }
    if(finalRaw!=null && finalRaw!=="" && Number.isFinite(final)) return Math.round(final*100)/100;
    if(basicRaw!=null && basicRaw!=="" && Number.isFinite(basic)) return Math.round(basic*1.18*100)/100;
    return "";
  };
  // Compatibility alias retained for the existing live release probe.
  const addonMasterCustomerRate = (a) => addonMasterRateForMode(a,"after");
  const chooseAddonMasterValue = (index,type,value) => {
    const match=addonOpts.find(a => a && a.active!==false && a.is_active!==false && !a.archived_at &&
      addonMasterType(a)===type && addonMasterDisplay(a)===value);
    setAddonRows(rows=>rows.map((x,xi)=>{
      if(xi!==index) return x;
      const masterRate=addonMasterRateForMode(match,addonTaxMode(x));
      return {...x,description:value,rate:match?String(masterRate):""};
    }));
  };
  const setAddonTaxMode = (index,mode) => {
    setAddonRows(rows=>rows.map((x,xi)=>{
      if(xi!==index) return x;
      const previous=addonTaxMode(x);
      const raw=String(x.rate??"").trim();
      if(previous===mode || raw==="") return {...x,tax_mode:mode};
      const n=Number(raw);
      if(!Number.isFinite(n)) return {...x,tax_mode:mode};
      const converted=mode==="before" ? n/1.18 : n*1.18;
      return {...x,tax_mode:mode,rate:String(Math.round(converted*100)/100)};
    }));
  };
  // quotation-addon-tax-mode-20260930
  // master-addon-price-autofill-20260930

  const total = (Number(qty) || 0) * (Number(rate) || 0) + addonRows.reduce((sum, a) => sum + (Number(a.qty) || 0) * addonRateAfterGST(a), 0);
  const subtotal = Math.round((total / 1.18) * 100) / 100;
  const gstAmt = Math.round((total - subtotal) * 100) / 100;

  const save = async () => {
    if (!customer.customer_name.trim()) return toast.error("Customer name required");
    if (customer.customer_phone.trim() && !/^\d{10}$/.test(customer.customer_phone.trim())) return toast.error("Phone number must be exactly 10 digits");
    if (customer.customer_gst.trim() && customer.customer_gst.trim().length !== 15) return toast.error("GST number must be exactly 15 characters");
    if (!mainC) return toast.error("Select the main container");
    if (!vehicle.trim()) return toast.error("Vehicle name is required");
    if (!(Number(rate) > 0)) return toast.error("Rate is required for the main item");
    for (const a of addonRows) {
      if (!a.description.trim() || !(Number(a.rate) > 0)) return toast.error("Each add-on needs a description and a rate");
    }
    setSaving(true);
    const payload = {
      ...customer, delivery_days: delivery.trim() || "15-30", quote_date: date,
      serial_no: serialCustom ? Number(serialCustom) : null,
      items: [
        { kind: "container", container_id: mainC, vehicle_name: vehicle.trim(), qty: Number(qty) || 1, rate: Math.round((Number(rate) / 1.18) * 100) / 100, specs: { ...specs, extra: extras.filter(e => e.trim()) } },
        ...addonRows.map(a => ({ kind: "addon", addon_type: a.type, description: a.description.trim(), qty: Number(a.qty) || 1, rate: addonRateBeforeGST(a) })),
      ],
    };
    try {
      if (editId) {
        await api.put(`/quotations/${editId}`, payload);
        toast.success("Quotation updated");
      } else {
        const { data } = await api.post("/quotations", payload);
        toast.success(`Quotation ${data.quote_number} saved`);
      }
      nav("/quotes");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? (d[0]?.msg || "Validation failed") : (d || "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">{editId ? "Edit Quotation" : "Create a new quote"}</h1>
        <p className="text-sm text-slate-500 mt-1">Surakhsha proforma invoice — PDF & Word generated from the master template.</p>
      </header>

      <div className="fc-card p-4 space-y-3.5">
        <div className="text-eyebrow">Quotation Number & Date</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Serial No.</label>
            <Input type="number" value={serialCustom} onChange={e => setSerialCustom(e.target.value)}
              placeholder={nextSerial ? String(nextSerial) : "…"} className={`${inputCls} font-mono-tab`} data-testid="quote-serial-input" />
            <div className="text-[11px] text-slate-400 mt-1">{editId ? `Serial ${nextSerial} (fixed)` : `Next available: ${nextSerial ?? "…"} — leave blank to auto-assign`}</div>
          </div>
          <div>
            <label className={labelCls}>Date (DD-MM-YYYY)</label>
            <Input value={date} onChange={e => setDate(e.target.value)} className={`${inputCls} font-mono-tab`} data-testid="quote-date-input" />
          </div>
        </div>
      </div>

      <div className="fc-card p-4 space-y-3.5" data-testid="quotation-builder-form">
        <div className="text-eyebrow">Customer</div>
        <div><label className={labelCls}>Customer Name *</label>
          <Input value={customer.customer_name} onChange={e => setCustomer({ ...customer, customer_name: e.target.value })} className={inputCls} data-testid="quote-customer-input" /></div>
        <div><label className={labelCls}>Address / Second Line (optional)</label>
          <textarea value={customer.customer_address} onChange={e => setCustomer({ ...customer, customer_address: e.target.value })} placeholder="City / address line" className={inputCls} data-testid="quote-customer-address-input"  rows={3} onKeyDown={e=>e.stopPropagation()} data-multiline-address="quotation-address-multiline-20260928" style={{width:"100%"}} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Phone (10 digits)</label>
            <Input value={customer.customer_phone} onChange={e => setCustomer({ ...customer, customer_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" placeholder="9876543210" className={`${inputCls} font-mono-tab`} data-testid="quote-phone-input" /></div>
          <div><label className={labelCls}>GST No. (15 chars)</label>
            <Input value={customer.customer_gst} onChange={e => setCustomer({ ...customer, customer_gst: e.target.value.toUpperCase().slice(0, 15) })} placeholder="32ABCDE1234F1Z5" className={`${inputCls} font-mono-tab`} data-testid="quote-gst-no-input" /></div>
        </div>
      </div>

      <div className="fc-card p-4 space-y-3.5">
        <div className="text-eyebrow">Item 1 — Main Container</div>
        <div><label className={labelCls}>Container (from catalog) *</label>
          <ContainerPicker catalog={catalog} value={mainC} onSelect={pickContainer} tid="quote-item-product-0" /></div>
        <div><label className={labelCls}>Base Vehicle *</label>
          <SearchSelect options={vehicleList} value={vehicle} onChange={setVehicle} placeholder="Search company vehicles or type custom…" tid="quote-vehicle-input" /></div>
        <div>
          <label className={labelCls}>External Dimensions</label>
          <Input value={specs.external} onChange={e => setSpecs({ ...specs, external: e.target.value })} placeholder="2250X1550X1600" className={`${inputCls} font-mono-tab`} data-testid="quote-spec-external" />
        </div>
        {SPEC_FIELDS.map(([label, key]) => (
          <div key={key}>
            <label className={labelCls}>{label}</label>
            <SearchSelect options={specOpts[{ inside: "Inside Finish", outside: "Out Side Finish", rear_door: "Rear Door", floor: "Floor", core: "Core", insulation: "Insulation Thickness" }[key]] || []}
              value={specs[key]} onChange={v => setSpecs({ ...specs, [key]: v })} placeholder={`Select ${label.toLowerCase()}…`} tid={`quote-spec-${key}`} />
          </div>
        ))}
        <div>
          <label className={labelCls}>Extra Specification Lines (optional)</label>
          {extras.map((ex, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input value={ex} onChange={e => setExtras(extras.map((x, xi) => xi === i ? e.target.value : x))} className={inputCls} data-testid={`quote-extra-${i}`} />
              <button type="button" onClick={() => setExtras(extras.filter((_, xi) => xi !== i))} className="text-rose-600 px-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setExtras([...extras, ""])} className="text-xs text-blue-600 font-medium" data-testid="quote-add-extra">+ Add specification line</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Qty (Nos)</label>
            <Input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} className={`${inputCls} font-mono-tab`} data-testid="quote-item-qty-0" /></div>
          <div><label className={labelCls}>Customer Rate (₹, after GST)</label>
            <Input type="number" value={rate} onChange={e => setRate(e.target.value)} className={`${inputCls} font-mono-tab`} data-testid="quote-item-rate-0" />
                  {String(rate ?? "").trim() !== "" && (
                    <p className="mt-1.5 text-xs leading-5 text-blue-700" aria-live="polite" data-testid="rate-in-words">
                      <span className="font-medium">Amount in words:</span> {rateAmountInWords(rate)}
                    </p>
                  )}</div>
        </div>
      </div>

      <div className="fc-card p-4 space-y-3.5">
        <div className="flex items-center justify-between">
          <div className="text-eyebrow">Add-ons (Reefer / Accessories / Other)</div>
          <button type="button" onClick={() => setAddonRows([...addonRows, { type: "Reefer Unit", description: "", qty: 1, rate: "", tax_mode: "after" }])}
            className="text-xs text-blue-600 font-medium flex items-center gap-1" data-testid="quote-add-addon-button">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        {addonRows.length === 0 && <div className="text-xs text-slate-400">No add-ons. Reefer units and accessories appear as items 2, 3… below the main container.</div>}
        {addonRows.map((a, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2.5 bg-slate-50/50" data-testid={`quote-addon-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">Item {i + 2}</span>
              <button type="button" onClick={() => setAddonRows(addonRows.filter((_, xi) => xi !== i))} className="text-rose-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <div className="grid grid-cols-3 gap-1 bg-slate-100 rounded-full p-1">
                {["Reefer Unit", "Accessory", "Other Charge"].map(t => (
                  <button type="button" key={t} onClick={() => setAddonRows(addonRows.map((x, xi) => xi === i ? { ...x, type: t, description: "", rate: "" } : x))}
                    className={`h-8 rounded-full text-[11px] font-medium transition-colors ${a.type === t ? "bg-blue-600 text-white" : "text-slate-600"}`}
                    data-testid={`quote-addon-type-${i}-${t.replace(/\s/g, "-")}`}>{t}</button>
                ))}
              </div>
            </div>
            <div><label className={labelCls}>Description</label>
              <SearchSelect options={addonDescriptions(a.type)} value={a.description} onChange={v => chooseAddonMasterValue(i,a.type,v)}
                placeholder="Search or type description…" tid={`quote-addon-desc-${i}`} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Qty</label>
                <Input type="number" min={1} value={a.qty} onChange={e => setAddonRows(addonRows.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x))} className={`${inputCls} font-mono-tab`} data-testid={`quote-addon-qty-${i}`} /></div>
              <div>
                <label className={labelCls}>Rate (₹)</label>
                <div className="mb-2 grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1" data-testid={`quote-addon-tax-mode-${i}`}>
                  {[["before","Before GST"],["after","After GST"]].map(([mode,label]) => (
                    <button type="button" key={mode} onClick={() => setAddonTaxMode(i,mode)}
                      className={`h-8 rounded-full text-[11px] font-medium transition-colors ${addonTaxMode(a)===mode ? "bg-blue-600 text-white" : "text-slate-600"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <Input type="number" value={a.rate} onChange={e => setAddonRows(addonRows.map((x, xi) => xi === i ? { ...x, rate: e.target.value } : x))}
                  className={`${inputCls} font-mono-tab`} data-testid={`quote-addon-rate-${i}`} />
                <p className="mt-1 text-[11px] text-slate-500">
                  {addonTaxMode(a)==="before" ? "18% GST will be added to this rate." : "This rate already includes 18% GST."}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="fc-card p-4 space-y-3.5">
        <div className="text-eyebrow">Terms</div>
        <div><label className={labelCls}>Delivery</label>
          <div className="flex items-center gap-2">
            <Input value={delivery} onChange={e => setDelivery(e.target.value)} className={`${inputCls} font-mono-tab w-28`} data-testid="quote-delivery-input" />
            <span className="text-sm text-slate-500">working Days from the confirmation of Order.</span>
          </div>
        </div>
        <p className="text-xs text-slate-500">Payment terms are fixed by the company (50% advance, balance on delivery). GST is always 18%. Admin can change these in Admin → Settings.</p>
      </div>

      <div className="fc-card p-5">
        <div className="text-eyebrow">Summary</div>
        <div className="mt-3 space-y-2 font-mono-tab text-slate-700 text-sm">
          <div className="flex justify-between"><span>Taxable value (calculated)</span><span>{fmtINR(subtotal)}</span></div>
          <div className="flex justify-between"><span>GST included @ 18%</span><span>{fmtINR(gstAmt)}</span></div>
          <div className="flex justify-between text-xl font-bold text-slate-900 pt-3 border-t border-slate-200 mt-2"><span>Total</span><span className="text-blue-600" data-testid="quote-total-amount-display">{fmtINR(total)}</span></div>
        </div>
        <button type="button" onClick={saveWorkDraft} className="h-11 px-4 rounded-xl border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50">Save Draft</button><button onClick={save} disabled={saving} data-testid="quote-save-submit-button"
          className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold mt-5 transition-colors disabled:opacity-60">
          {saving ? "Saving…" : editId ? "Update Quotation" : "Save Quotation"}
        </button>
      </div>
    </div>
  );
}

// quotation-address-multiline-20260930
